import React, { useState, useRef } from 'react';
import { compressImage, formatBytes } from '../utils/imageCompressor';
import { uploadToImgbb } from '../utils/imgbbUploader';
import { 
  UploadCloud, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  Sparkles, 
  Download, 
  Key, 
  X
} from 'lucide-react';

export default function ImageUploadCompressor({ onImageUploaded, label = "Upload Image with Auto-Compressor" }) {
  const [isCompressing, setIsCompressing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [compressionResult, setCompressionResult] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [error, setError] = useState('');
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('imgbb_api_key') || '');
  
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setIsCompressing(true);
    setUploadedUrl('');

    try {
      // Compress image client-side: Max 1600px width/height, 82% quality (HD clarity, 80-95% lighter)
      const result = await compressImage(file, {
        maxWidth: 1600,
        maxHeight: 1600,
        quality: 0.82
      });
      setCompressionResult(result);
      setIsCompressing(false);

      // Auto-upload if API key is already configured
      const activeKey = apiKey || process.env.REACT_APP_IMGBB_API_KEY || localStorage.getItem('imgbb_api_key');
      if (activeKey) {
        performUpload(result.compressedBlob, activeKey);
      } else {
        setShowKeyInput(true);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error compressing image');
      setIsCompressing(false);
    }
  };

  const performUpload = async (blob, keyToUse) => {
    setIsUploading(true);
    setError('');

    try {
      const res = await uploadToImgbb(blob, keyToUse);
      setUploadedUrl(res.url);
      setIsUploading(false);
      setShowKeyInput(false);
      
      // Auto-populate parent form input
      if (onImageUploaded) {
        onImageUploaded(res.url);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to upload to ImgBB');
      setIsUploading(false);
    }
  };

  const handleSaveKeyAndUpload = (e) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      setError('Please enter a valid ImgBB API key');
      return;
    }
    localStorage.setItem('imgbb_api_key', apiKey.trim());
    if (compressionResult) {
      performUpload(compressionResult.compressedBlob, apiKey.trim());
    }
  };

  const handleDownloadCompressed = () => {
    if (!compressionResult) return;
    const link = document.createElement('a');
    link.href = compressionResult.previewUrl;
    link.download = compressionResult.compressedFile.name || 'compressed-image.jpg';
    link.click();
  };

  const handleReset = () => {
    setCompressionResult(null);
    setUploadedUrl('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      border: '1.5px dashed var(--border-color)',
      borderRadius: '14px',
      padding: '16px',
      marginBottom: '14px'
    }}>
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={16} style={{ color: 'var(--primary-color)' }} />
          <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'Poppins' }}>
            {label}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setShowKeyInput(!showKeyInput)}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            padding: '2px 6px',
            borderRadius: '6px'
          }}
          title="Configure ImgBB API Key"
        >
          <Key size={12} /> {apiKey ? 'API Key Saved' : 'Set ImgBB Key'}
        </button>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/jpg"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Upload / Dropzone Trigger */}
      {!compressionResult && (
        <div 
          onClick={() => fileInputRef.current?.click()}
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid var(--border-color)',
            borderRadius: '10px',
            padding: '14px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: '#e0f2fe',
            color: 'var(--primary-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isCompressing ? <Loader2 size={20} className="animate-spin" /> : <UploadCloud size={20} />}
          </div>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {isCompressing ? 'Compressing high-definition image...' : 'Click to Select Image for Auto-Compression'}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Auto-scales large phone photos to 1600px & 82% quality (saves ~80-95% file size for fast loading)
          </span>
        </div>
      )}

      {/* Compression Result & Upload Feedback */}
      {compressionResult && (
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid var(--border-color)',
          borderRadius: '10px',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Thumbnail Preview */}
            <img 
              src={compressionResult.previewUrl} 
              alt="Compressed Preview" 
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '8px',
                objectFit: 'cover',
                border: '1px solid var(--border-color)'
              }}
            />

            {/* Compression Stats */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                <span style={{
                  backgroundColor: '#dcfce7',
                  color: '#15803d',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  padding: '2px 8px',
                  borderRadius: '999px'
                }}>
                  Saved {compressionResult.savedPercentage}%
                </span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  {formatBytes(compressionResult.originalSize)} ➔ {formatBytes(compressionResult.compressedSize)}
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Resolution: {compressionResult.width} × {compressionResult.height}px (High Definition)
              </div>
            </div>

            {/* Clear Button */}
            <button
              type="button"
              onClick={handleReset}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                padding: '4px'
              }}
              title="Remove"
            >
              <X size={16} />
            </button>
          </div>

          {/* Upload Status */}
          {isUploading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary-color)', fontSize: '0.82rem', fontWeight: 600 }}>
              <Loader2 size={16} className="animate-spin" />
              <span>Uploading compressed image to ImgBB...</span>
            </div>
          )}

          {uploadedUrl && (
            <div style={{
              backgroundColor: '#ecfdf5',
              border: '1px solid #a7f3d0',
              borderRadius: '8px',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.8rem',
              color: '#065f46',
              fontWeight: 700
            }}>
              <CheckCircle2 size={16} style={{ color: '#059669', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Uploaded to ImgBB! URL applied to form below.
              </div>
            </div>
          )}

          {/* Secondary Actions: Download / Re-upload */}
          {!uploadedUrl && !isUploading && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => {
                  const activeKey = apiKey || localStorage.getItem('imgbb_api_key');
                  if (activeKey) {
                    performUpload(compressionResult.compressedBlob, activeKey);
                  } else {
                    setShowKeyInput(true);
                  }
                }}
                className="btn-premium btn-premium-primary"
                style={{ height: '34px', padding: '0 14px', fontSize: '0.8rem' }}
              >
                <UploadCloud size={14} /> Upload to ImgBB
              </button>
              <button
                type="button"
                onClick={handleDownloadCompressed}
                className="btn-premium btn-premium-secondary"
                style={{ height: '34px', padding: '0 12px', fontSize: '0.8rem' }}
                title="Download compressed file to upload manually"
              >
                <Download size={14} /> Download File
              </button>
            </div>
          )}
        </div>
      )}

      {/* ImgBB API Key Configuration Form */}
      {showKeyInput && (
        <form onSubmit={handleSaveKeyAndUpload} style={{
          marginTop: '10px',
          backgroundColor: '#ffffff',
          border: '1px solid #fed7aa',
          borderRadius: '8px',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#9a3412' }}>
            ImgBB API Key Required for Direct Upload:
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input
              type="text"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Paste your free ImgBB API Key here"
              className="dashboard-input"
              style={{ flex: 1, height: '34px', fontSize: '0.8rem', padding: '0 10px' }}
              required
            />
            <button
              type="submit"
              className="btn-premium btn-premium-primary"
              style={{ height: '34px', padding: '0 12px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
            >
              Save & Upload
            </button>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            Get a 100% free API key in 10 seconds at <a href="https://api.imgbb.com/" target="_blank" rel="noreferrer" style={{ color: 'var(--primary-color)', textDecoration: 'underline' }}>api.imgbb.com</a>. Saved securely in your browser.
          </div>
        </form>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: '#dc2626',
          fontSize: '0.75rem',
          marginTop: '8px',
          fontWeight: 600
        }}>
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
